import type { ReactNode } from "react";
import { Image as ImageIcon, ListChecks } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  SegmentedFilter,
  SegmentedFilterOption,
} from "@/components/common/segmented-filter/segmented-filter";

/** `null` is the All option — no kind filter at all. */
export type ReportKindSelection = "issues" | "photos" | null;

interface ReportKindFilterProps {
  value: ReportKindSelection;
  onChange: (value: ReportKindSelection) => void;
  /** Undefined while a count is not known. */
  counts: {
    all: number | undefined;
    issues: number | undefined;
    photos: number | undefined;
  };
}

/**
 * All / Issues / Photos above the Reports list.
 *
 * The same track-and-segment look as the Issues page's status row, so the two
 * filters read as one family. Full width in thirds on a phone, as wide as its
 * options from 640px. Options never disappear at zero: a vanishing option
 * would make the row reflow as reports are made. See [[report-kind-filter.spec]].
 */
export function ReportKindFilter({
  value,
  onChange,
  counts,
}: ReportKindFilterProps) {
  return (
    <SegmentedFilter
      aria-label="Filter reports by kind"
      className="w-full sm:w-max"
    >
      <KindOption
        selected={value === null}
        onSelect={() => onChange(null)}
        label="All"
        count={counts.all}
      />
      {/* Same icons and colours as the rows' kind icon and the New Report
          sheet, so the filter reads as the legend for the list below. */}
      <KindOption
        selected={value === "issues"}
        onSelect={() => onChange("issues")}
        label="Issues"
        count={counts.issues}
        icon={
          <ListChecks
            data-slot="kind-icon"
            aria-hidden="true"
            className="size-3.5 shrink-0 text-kind-issues"
          />
        }
      />
      <KindOption
        selected={value === "photos"}
        onSelect={() => onChange("photos")}
        label="Photos"
        count={counts.photos}
        icon={
          <ImageIcon
            data-slot="kind-icon"
            aria-hidden="true"
            className="size-3.5 shrink-0 text-kind-photos"
          />
        }
      />
    </SegmentedFilter>
  );
}

function KindOption({
  selected,
  onSelect,
  label,
  count,
  icon,
}: {
  selected: boolean;
  onSelect: () => void;
  label: string;
  count: number | undefined;
  /** The kind's icon; All has none. */
  icon?: ReactNode;
}) {
  return (
    <SegmentedFilterOption
      selected={selected}
      onSelect={onSelect}
      className="flex-1 justify-center sm:flex-none sm:px-4"
    >
      {icon}
      <span>{label}</span>
      {/* Omitted, not zeroed, while unknown: a 0 that then becomes 35 is a
          lie the user may act on. */}
      {count != null && (
        <span
          className={cn("opacity-60", selected && "dark:text-primary dark:opacity-100")}
        >
          {count}
        </span>
      )}
    </SegmentedFilterOption>
  );
}
